import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

// Prevent scrolling to ID after component is hidden
function clearHash() {
  if (window.location.hash) {
    window.history.replaceState(
      '',
      document.title,
      window.location.pathname + window.location.search
    );
  }
}

/**
 * Use browser back button to control showing/hiding the component
 *
 * @param componentId Unique component ID, e.g. "Gallery Overlay"
 *
 * @return [boolean, (boolean) => void] Show component & show component setter
 */
function useShowComponentWithHistory(
  componentId: string
): [boolean, (_: boolean) => void] {
  const [showComponent, setShowComponentValue] = useState(false);
  const router = useRouter();

  const setShowComponent = useCallback(
    (show: boolean) => {
      if (show) {
        clearHash();
        router.push(`#${componentId}`, undefined, { shallow: true });
      } else {
        router.back();
      }
      setShowComponentValue(show);
    },
    [componentId, router]
  );

  useEffect(() => {
    function handler() {
      setShowComponentValue(false);
    }

    window.addEventListener('popstate', handler, false);

    return () => {
      window.removeEventListener('popstate', handler);
    };
  }, []);

  return [showComponent, setShowComponent];
}

export default useShowComponentWithHistory;
